import * as theme from '../../../../theme';
import logo from '../../../../images/tb-white-main-word-only.png';
import rhLogo from '../../../../images/rh-logo.png';
import weeklySupport from '../../../../images/weekly-support-call.png';
import logo2 from '../../../../images/tb-footer-gold.png';
import maregyOtf from '../../../../theme/MAREGY.otf';
import beautifullyOtf from '../../../../theme/Beautifully.otf';

export function generateSupplementScheduleHTML(data, fullName, dateStr, brand = 'TruBalance') {

  // Extract data sections with deeper defaults to handle partial/empty objects
  const topItems = data.topItems || [];
  const imprintRemedy = {
    drops: data.imprintRemedy?.drops || '',
    am: data.imprintRemedy?.am || false,
    pm: data.imprintRemedy?.pm || false,
    duration: data.imprintRemedy?.duration || ''
  };
  const homeopathics = data.homeopathics || [];
  const asea = {
    summary: data.asea?.summary || false,
    liquid: {
      enabled: data.asea?.liquid?.enabled || false,
      first: data.asea?.liquid?.first || '',
      second: data.asea?.liquid?.second || ''
    },
    gel: {
      enabled: data.asea?.gel?.enabled || false,
      first: data.asea?.gel?.first || '',
      second: data.asea?.gel?.second || ''
    }
  };
  const herbs = {
    groups: data.herbs?.groups || [],
    specialNotes: data.herbs?.specialNotes || []
  };
  const recommendations = data.recommendations || { therapies: [], recommendations: [] };
  const keyNotes = data.keyNotes || { priorities: [], notes: [] };
  const itemsToOrder = data.itemsToOrder || [];
  const itemsToShip = data.itemsToShip || [];
  const followUp = data.followUp || '';

  // Check if additional page content exists
  const hasAdditionalRecommendations = recommendations.therapies.length > 0 || recommendations.recommendations.length > 0;
  const hasKeyNotes = keyNotes.priorities.length > 0 || keyNotes.notes.length > 0;
  const hasItemsToOrder = itemsToOrder.length > 0;
  const hasItemsToShip = itemsToShip.length > 0;
  const hasAdditionalPage = hasAdditionalRecommendations || hasKeyNotes || hasItemsToOrder || hasItemsToShip;

  // Helper to format boolean to checkmark
  const formatCheck = (bool) => bool ? '✔' : '';

  // Format Top Items
  const formatTopItems = () => {
    if (topItems.length === 0) return '<p class="secondary-text">None</p>';
    return topItems.map((item, index) => `
      <div class="priority-item">
        <div class="priority-circle">${index + 1}</div>
        <div class="priority-text">${item}</div>
      </div>
    `).join('');
  };

  // Format Imprint + Homeopathics Table
  const formatImprintHomeopathics = () => {
    let rows = '';
    if (imprintRemedy.drops.length > 0) {
      const amPm = `${imprintRemedy.drops} drops ${imprintRemedy.am ? 'AM' : ''}${imprintRemedy.am && imprintRemedy.pm ? ' and ' : ''}${imprintRemedy.pm ? 'PM' : ''}`;
      rows += `
        <tr>
          <td><b>IMPRINT REMEDY</b></td>
          <td><b>${amPm}</b></td>
          <td>${formatCheck(imprintRemedy.am)}</td>
          <td>${formatCheck(imprintRemedy.pm)}</td>
          <td>${imprintRemedy.duration} weeks</td>
        </tr>
      `;
    }
    homeopathics.forEach(h => {
      const amPm = `${h.dose} ${h.am ? 'AM' : ''}${h.am && h.pm ? ' + ' : ''}${h.pm ? 'PM' : ''}`;
      rows += `
        <tr>
          <td><b>${h.item}</b></td>
          <td>${amPm}</td>
          <td>${formatCheck(h.am)}</td>
          <td>${formatCheck(h.pm)}</td>
          <td>${h.duration} weeks</td>
        </tr>
      `;
    });
    return `
      <table class="schedule-table">
        <thead>
          <tr>
            <th></th>
            <th>DOSE</th>
            <th>AM</th>
            <th>PM</th>
            <th>DURATION</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
    `;
  };

  // Format ASEA Section
  const formatAsea = () => {
    let content = '';
    if (asea.liquid.enabled || asea.gel.enabled) {
      content += '<div class="section" style="margin-top: 20px; margin-bottom: 20px;"><p class="header-text">ASEA</p>';
      if (asea.summary) {
        content += `<p class="secondary-text" style="margin-bottom: 8px;">Take <b>5 minutes</b> away from food + swish in mouth for 30 seconds for best results. <b>See PDF for detailed instructions.</b> If after the recommended number of weeks it still tastes like metal or pool water, <b>keep taking it, until the ASEA taste like water.</b></p>`;
      }
      if (asea.liquid.enabled) {
        content += `
          <p class="secondary-text" style="margin-bottom: 6px;"><span class="subheading-text">LIQUID DOSE -</span> <b>${asea.liquid.first}</b> ${asea.liquid.second}</p>
        `;
      }
      if (asea.gel.enabled) {
        content += `
          <p class="secondary-text" style="margin-bottom: 6px;"><span class="subheading-text">RENU28 GEL -</span> ${asea.gel.first} <b>${asea.gel.second}</b></p>
        `;
      }
      content += '</div>';
    } else {
      content+= '<div style="margin-top: 16px;"></div>'
    }
    return content;
  };

  // Format Herbs + Supplements
  const formatHerbs = () => {
    let tables = '';
    herbs.groups.forEach(group => {
      if (group.items && group.items.length > 0) {
        let rows = '';
        group.items.forEach(item => {
          rows += `
            <tr>
              <td><b>${item.item}</b>${item.specialNote ? '&nbsp;-&nbsp;<span class="special-note">' + item.specialNote + '</span>' : ''}</td>
              <td>${item.dose}</td>
              <td>${formatCheck(item.am)}</td>
              <td>${formatCheck(item.midDay)}</td>
              <td>${formatCheck(item.pm)}</td>
              <td>${formatCheck(item.bedtime)}</td>
              <td>${item.duration}</td>
            </tr>
          `;
        });
        tables += `
          <table class="schedule-table">
            <thead>
              <tr>
                <th style="width: 360px;">${group.showHeading && group.heading ? group.heading : ''}</th>
                <th style="width: 120px;">DOSE</th>
                <th>AM</th>
                <th>NOON</th>
                <th>PM</th>
                <th>BED</th>
                <th>DURATION</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
        `;
      }
    });
    return tables;
  };

  // Format Special Notes
  const formatSpecialNotes = () => {
    if (herbs.specialNotes.length === 0) return '';
    return `
      <div class="section">
        <p class="heading-text" style="margin-bottom: 0px; margin-top: 14px;">SPECIAL NOTES:</p>
        <p class="secondary-text">
          ${herbs.specialNotes.join('<br/>')}
        </p>
      </div>
    `;
  };

  // Format Additional Recommendations
  const formatAdditionalRecommendations = () => {
    if (!hasAdditionalRecommendations) return '';
    let therapiesHTML = '';
    if (recommendations.therapies.length > 0) {
      therapiesHTML = `
        <p class="heading-text" style="margin-bottom: 5px;">Beneficial Therapies:</p>
        <ol class="item-list">
          ${recommendations.therapies.map(therapy => `<li>${therapy}</li>`).join('')}
        </ol>
      `;
    }
    let recommendationsHTML = '';
    if (recommendations.recommendations.length > 0) {
      recommendationsHTML = `
        <p class="heading-text" style="margin-bottom: 5px; margin-top: 10px;">Recommendations</p>
        ${recommendations.recommendations.map(rec => `<p class="secondary-text notes">${rec}</p>`).join('')}
      `;
    }
    return `
      <div class="section">
        <p class="header-text">Additional Recommendations</p>
        ${therapiesHTML}
        ${recommendationsHTML}
      </div>
    `;
  };

  // Format Key Notes From Scan
  const formatKeyNotes = () => {
    if (!hasKeyNotes) return '';
    let prioritiesHTML = '';
    if (keyNotes.priorities.length > 0) {
      prioritiesHTML = `
        <p class="heading-text" style="margin-bottom: 5px;">Priorities:</p>
        <ol class="item-list">
          ${keyNotes.priorities.map(priority => `<li>${priority}</li>`).join('')}
        </ol>
      `;
    }
    let notesHTML = '';
    if (keyNotes.notes.length > 0) {
      notesHTML = `
        <p class="heading-text" style="margin-bottom: 5px; margin-top: 10px;">Notes</p>
          ${keyNotes.notes.map(note => `<p class="secondary-text notes">${note}</p>`).join('')}
      `;
    }
    return `
      <div class="section sectionSpacing">
        <p class="header-text">Key Notes From Scan</p>
        ${prioritiesHTML}
        ${notesHTML}
      </div>
    `;
  };

  // Format Items Boxes
  const formatItemsBoxes = () => {
    let orderHTML = '';
    if (hasItemsToOrder) {
      orderHTML = `
        <div class="items-box">
          <p class="header-text header-smaller">Items You Order</p>
          ${itemsToOrder.map(item => `<p class="secondary-text">${item}</p>`).join('')}
        </div>
      `;
    }
    let shipHTML = '';
    if (hasItemsToShip) {
      shipHTML = `
        <div class="items-box">
          <p class="header-text header-smaller">I Ship To You</p>
          ${itemsToShip.map(item => `<p class="secondary-text">${item}</p>`).join('')}
        </div>
      `;
    }
    if (orderHTML || shipHTML) {
      return `
        <div class="items-row">
          ${orderHTML}
          ${shipHTML}
        </div>
      `;
    }
    return '';
  };

  const fontUrl = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap';
  const importRule = `@import url('${fontUrl}');`;

  const fontFaceRule = `
    @font-face {
      font-family: 'Maregy';
      src: url('${maregyOtf}') format('opentype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `;

  const fontFaceBeautifully = `
    @font-face {
      font-family: 'Beautifully';
      src: url('${beautifullyOtf}') format('opentype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `;

  // Brand-specific toggles
  const logoSrc = brand === 'TruBalance' ? logo : rhLogo;
  const logoSrc2 = brand === 'TruBalance' ? logo2 : rhLogo;
  const purpleColor = brand === 'TruBalance' ? theme.Colors.Purple : theme.Colors.RHPurple;
  const grayDark = brand === 'TruBalance' ? theme.Colors.GrayDark : theme.Colors.RHBlack;
  const logoMaxHeight = brand === 'TruBalance' ? '14px' : '26px';
  const logo2MaxHeight = brand === 'TruBalance' ? '90px' : '40px';
  const logo2Right = brand === 'TruBalance' ? '10px' : '25px';
  const logoTop = brand === 'TruBalance' ? '100px' : '96px';
  const logoLeft = brand === 'TruBalance' ? '238px' : '207px';
  const logoTopPrint = brand === 'TruBalance' ? '98px' : '93px';
  const logoLeftPrint = brand === 'TruBalance' ? '242px' : '216px';
  const logoMaxHeightPrint = brand === 'TruBalance' ? '10.5px' : '20px';

  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Supplement Schedule</title>
      <style>
        ${importRule}
        ${fontFaceRule}
        ${fontFaceBeautifully}
        body { font-family: Montserrat, Arial, sans-serif; background: white; margin: 0; padding: 0; }
        .container { max-width: 800px; margin: 0 auto; padding: 10px; }
        .full-width { width: 100%; }
        .header-bar { background-color: ${grayDark}; height: auto; width: 100%; display: flex; justify-content: center; align-items: center; padding: 20px; box-sizing: border-box; }
        .page-header { background-color: ${grayDark}; height: 98px; width: 100%; display: flex; justify-content: center; align-items: center; padding: 20px; box-sizing: border-box; }
        .header-container { display: flex; justify-content: space-between; align-items: center; max-width: 800px; width: 100%; height: 92px; position: relative; }
        .header-left { flex: 2; display: flex; flex-direction: column; align-items: flex-start; }
        .header-title { }
        .header-title.big { font-size: 120px; top: -20px; }
        .header-title.small { font-size: 80px; top: 0px; }
        .header-title.first { font-family: Beautifully, Montserrat, Arial; font-size: 110px; color: ${theme.Colors.Gold}; position: absolute; top: -32px; left: 10px; }
        .header-title.second { font-family: Maregy, Montserrat, Arial; font-size: 35px; color: white; letter-spacing: 5px; text-transform: uppercase; position: absolute; top: 59px; left: 140px; }
        .logo { max-height: ${logoMaxHeight}; position: absolute; top: ${logoTop}; left: ${logoLeft}; }
        .logo2 { max-height: ${logo2MaxHeight}; padding-right: ${logo2Right}; }
        .weeklySupport { max-height: 50px; margin-left: 10px; }
        .header-right { flex: 1; display: flex; flex-direction: column; align-items: flex-end; color: white; font-size: 16px; }
        .header-detail-container { margin-top: 7px; display: flex; flex-direction: row; width: 100%; }
        .header-detail-heading { text-transform: uppercase; }
        .header-detail-left { flex: 0.75 }
        .header-detail-right { flex: 2; }
        .print-btn { position: absolute; top: 20px; right: 20px; padding: 8px 16px; background: ${purpleColor}; border-radius: 4px; color: white; border: none; cursor: pointer; }
        .print-btn:hover { background: ${theme.Colors.GoldDark}; }
        .header-text { font-size: 22px; margin-top: 7px; font-family: Maregy, Montserrat, Arial; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1.2px; -webkit-text-stroke: 1px black; }
        .header-smaller { font-size: 18px; text-align: center; }
        .intro-text { font-size: 12px; margin-bottom: 15px; text-align: justify; }
        .secondary-text { font-size: 13px; margin-bottom: 15px; text-align: justify; line-height: 1.3; margin-top: 0px; }
        .subheading-text { font-size: 14px; font-weight: bold; text-transform: uppercase; margin-bottom: 5px; }
        .smaller { font-size: 11px; }
        .heading-text { font-size: 14px; text-transform: uppercase; margin-top: 8px; margin-bottom: 8px; }
        .notes-list { list-style-type: disc; padding-left: 20px; margin: 0; font-size: 13px; line-height: 1.3; }
        .notes-list li { margin-bottom: 10px; }
        .schedule-table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 10px; }
        .schedule-table th, .schedule-table td { border: 1px solid #ccc; padding: 3px; padding-left: 6px; text-align: center; font-size: 13px; }
        .schedule-table th { background-color: #f5f5f5; text-transform: uppercase; }
        .schedule-table td:first-child { text-align: left; }
        .schedule-table td:nth-child(2) { text-align: left; }
        .priorities-list { display: flex; flex-direction: row; gap: 20px; margin-top: 10px; margin-bottom: 20px; flex-wrap: nowrap; }
        .priority-item { display: flex; align-items: center; gap: 10px; }
        .priority-circle { color: ${purpleColor}; width: 25px; height: 25px; border-radius: 50%; border: 4px double ${purpleColor}; display: flex; justify-content: center; align-items: center; font-size: 15px; flex-shrink: 0; }
        .priority-text { text-transform: uppercase; font-size: 14px; }
        .weekly { display: flex; flex-direction: row; justify-content: center; align-items: center; font-weight: bold; text-transform: uppercase; font-size: 13px; }
        .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .item-list { list-style-type: disc; padding-left: 20px; margin: 0; font-size: 13px; line-height: 1.3; }
        .item-list li { margin-bottom: 5px; }
        .items-row { display: flex; justify-content: space-around; gap: 20px; margin-top: 20px; align-items: start; }
        .items-box { flex: 1; border: 1px solid #000; border-radius: 8px; padding: 15px; width: 300px; max-width: 300px; }
        .items-box p { text-align: center }
        .notes { margin-top: 0px; margin-bottom: 6px; }
        .sectionSpacing { margin-top: 25px; margin-bottom: 25px; }
        .special-note { font-size: 11px; font-weight: normal; }
        @media print { 
          .print-btn { display: none; } 
          @page { margin: 0; size: letter portrait;}
          body { margin: 0; padding: 0; display: flex; flex-direction: column; height: 100%; }
          .container { max-width: 100%; width: 100%; height: auto; flex: 1; box-sizing: border-box; padding: 10px 40px; }
          .header-bar { justify-content: space-between; padding: 15px 10px; flex-shrink: 0; page-break-after: avoid; }
          .header-container { max-width: 100% }
          .header-title.big { font-size: 100px; top: -10px; }
          .header-title.small { font-size: 65px; top: 15px; }
          .header-title.first { font-size: 85px; top: -9px; left: 50px; }
          .header-title.second { font-size: 28px; top: 61px; left: 146px; }
          .logo { max-height: ${logoMaxHeightPrint}; top: ${logoTopPrint}; left: ${logoLeftPrint}; }
          .logo2 { top: 10px; position: relative; }
          .header-right { font-size: 12px; }
          .header-text { font-size: 17px; margin-bottom: 6px; }
          .header-smaller { font-size: 15px; }
          .intro-text { font-size: 9.7px; line-height: 1.1 }
          .secondary-text { font-size: 11px; line-height: 1.2 }
          .subheading-text { font-size: 12px; }
          .smaller { font-size: 9px; }
          .heading-text { font-size: 12px; }
          .notes-list li { font-size: 11px; }
          .schedule-table th, .schedule-table td { font-size: 11px; padding: 2px; padding-left: 5px; }
          .priority-text { font-size: 11px; }
          .priority-circle { width: 22px; height: 22px; border: 3px double ${purpleColor}; font-size: 10px; }
          .priorities-list { gap: 15px; }
          .weekly { font-size: 11px; }
          .item-list li { font-size: 11px; }
          .special-note { font-size: 9px; font-weight: normal; }
          * { box-sizing: border-box; orphans: 1; widows: 1; }
          .page-header { page-break-before: always; }
        }
      </style>
    </head>
    <body>
      <div class="header-bar">
        <div class="header-container">
          <div class="header-left">
            <div class="header-title big first">Supplement</div>
            <div class="header-title big second">Schedule</div>
          </div>
          <div class="header-right">
            <div class="header-detail-container"><span class="header-detail-heading">Name:</span>&nbsp;&nbsp;${fullName}</div>
            <div class="header-detail-container"><span class="header-detail-heading">Date:</span>&nbsp;&nbsp;${dateStr}</div>
            <div class="header-detail-container"><span class="header-detail-heading">Follow-Up:</span>&nbsp;&nbsp;${followUp}</div>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="full-width">
          <div class="top-section">
            <div class="header-row" style="align-items: end;">
              <p class="header-text">Top Support Items</p>
            </div>
            <p class="secondary-text">These are the top items your body prioritizes right now. You can choose all 4 right away, or start with 1 and ease into the rest. Work within your budget and treatment goals, but remember these are the top things your body needs, so place most importance on these 4 items. Dosage listed in schedule below.</p>
            <div class="priorities-list">
              ${formatTopItems()}
            </div>
          </div>
          <div class="section">
            <p class="header-text">Imprint + Homeopathics</p>
            <p class="secondary-text">Ideally take 5+ minutes before or after food/herbs/coffee/brushing teeth. You can take the imprint and all homeopathics at the same time. <b>Start with 1 DROP of the imprint</b>, and work up by 1 drop every few days as tolerated.</p>
            ${formatImprintHomeopathics()}
          </div>
          ${formatAsea()}
          <div class="section">
            <p class="header-text">Herbs + Supplements</p>
            <p class="secondary-text">Ideally take herbs + supplements 30+ minutes before meals or between meals (unless specified below). All items can be taken together, unless specific instructions included for an item. Can take with hydrogen or lemon water.</p>
            ${formatHerbs()}
          </div>
          ${formatSpecialNotes()}
        </div>
      </div>
  `;

  if (hasAdditionalPage) {
    html += `
      <div class="page-header">
        <div class="header-container">
          <div class="header-left">
          </div>
          <div class="header-right">
            <img src="${logoSrc2}" alt="Logo" class="logo2">
          </div>
        </div>
      </div>
      <div class="container">
        <div class="full-width">
          ${formatAdditionalRecommendations()}
          ${formatKeyNotes()}
          <div class="section sectionSpacing">
            <p class="header-text">Detox Reactions</p>
            <p class="secondary-text notes">You may experience detox reactions or unpleasant detox symptoms. This is why it is recommended to start with ONLY 1 DROP of your imprint remedy, and slowly work up to desired dose as tolerated. This is why it is also important to remain on the drainage remedy.</p>
            <p class="secondary-text notes">Refer to the detox information sheet for additional tips to help with detox.</p>
          </div>
          <div class="section sectionSpacing">
            <p class="header-text">Important Notes</p>
            <p class="secondary-text notes">Hydration is important when detoxing. Fruit contains a lot of water and that counts. If you are thirsty it means you need more water/fruit.</p>
            <p class="secondary-text notes">Make sure you are eliminating at least once daily. If you are not please contact your practitioner.</p>
            <p class="secondary-text notes">It is important to remain on drainage item when taking imprint remedy. Please do not discontinue drainer.</p>
            <p class="secondary-text notes">Stay on all products for scheduled amount of time.</p>
            <p class="secondary-text notes">If you are unsure about using this supplementation, please contact your primary care physician before starting.</p>
            <p class="secondary-text notes"><b>The information in this report is related to energetically balancing the body and is not to be diagnostic or replace the advice of your medical doctor.</b></p>
          </div>
          ${formatItemsBoxes()}
        </div>
      </div>
    `;
  }

  html += `
      <button class="print-btn" onclick="window.print()">Print</button>
    </body>
    </html>
  `;

  return html;
}